import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, CloudOff, Check, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { offlineQueueService, type QueuedRequest } from '../../services/offlineQueueService';

/**
 * OfflineQueueManager
 *
 * Floating panel that shows queued offline actions and lets the user
 * manually retry or dismiss individual items. Auto-hides when the
 * queue is empty and the device is online.
 */
export const OfflineQueueManager: React.FC = () => {
  const [queue, setQueue] = useState<QueuedRequest[]>(offlineQueueService.getQueue());
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Subscribe to queue changes
  useEffect(() => {
    const unsubscribe = offlineQueueService.onQueueChange((updatedQueue) => {
      setQueue(updatedQueue);
      setIsProcessing(offlineQueueService.isProcessing());
    });

    return unsubscribe;
  }, []);

  // Track online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetryAll = useCallback(async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    const successCount = await offlineQueueService.processQueue();
    setIsProcessing(false);

    if (successCount > 0) {
      toast.success(
        `Synced ${successCount} ${successCount === 1 ? 'action' : 'actions'} successfully`,
        { duration: 3000 }
      );
    } else if (queue.length > 0) {
      toast.error('Could not sync actions. Will retry automatically.', { duration: 3000 });
    }
  }, [isProcessing, queue.length]);

  const handleDismissItem = useCallback((id: string) => {
    offlineQueueService.removeItem(id);
  }, []);

  const handleClearAll = useCallback(() => {
    offlineQueueService.clearQueue();
    toast('Cleared all queued actions', { duration: 2000 });
  }, []);

  // Don't render if online and queue is empty
  if (isOnline && queue.length === 0) {
    return null;
  }

  // Don't render if queue is empty regardless of state
  if (queue.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-[90] sm:left-auto sm:right-4 sm:max-w-sm"
      role="status"
      aria-label="Offline queue status"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-100 dark:border-amber-800/50">
          <div className="flex items-center gap-2">
            <CloudOff className="w-4 h-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {queue.length} pending {queue.length === 1 ? 'action' : 'actions'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {isOnline && (
              <button
                onClick={handleRetryAll}
                disabled={isProcessing}
                className="p-1.5 rounded-lg text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800/50
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Retry all queued actions"
                title="Retry all"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            )}
            <button
              onClick={handleClearAll}
              className="p-1.5 rounded-lg text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800/50 transition-colors"
              aria-label="Clear all queued actions"
              title="Clear all"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Queue items */}
        <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {queue.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-750"
            >
              <div className="flex-1 min-w-0 mr-2">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                  {formatRequestLabel(item)}
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                  {formatTimestamp(item.timestamp)}
                  {item.retryCount > 0 && (
                    <span className="ml-1.5 text-amber-500">
                      retry {item.retryCount}/{item.maxRetries}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => handleDismissItem(item.id)}
                className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                aria-label={`Dismiss queued ${item.method} request`}
              >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border-t border-blue-100 dark:border-blue-800/50">
            <div className="flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 dark:text-blue-400" aria-hidden="true" />
              <span className="text-xs text-blue-700 dark:text-blue-300">Syncing...</span>
            </div>
          </div>
        )}

        {/* Sync success hint when online */}
        {isOnline && !isProcessing && queue.length > 0 && (
          <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border-t border-green-100 dark:border-green-800/50">
            <div className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" aria-hidden="true" />
              <span className="text-xs text-green-700 dark:text-green-300">
                Online - tap retry to sync
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Create a human-readable label from a queued request. */
function formatRequestLabel(request: QueuedRequest): string {
  const methodLabels: Record<string, string> = {
    POST: 'Create',
    PUT: 'Update',
    DELETE: 'Delete',
  };

  const action = methodLabels[request.method] || request.method;

  // Try to extract a meaningful resource name from the URL path
  const urlPath = request.url.replace(/https?:\/\/[^/]+/, '');
  const segments = urlPath.split('/').filter(Boolean);
  // Remove "v1" prefix
  const meaningful = segments.filter((s) => s !== 'v1');
  const resource = meaningful.length > 0 ? meaningful[meaningful.length - 1] : 'item';

  // Clean up UUID-like segments
  const cleaned = resource.replace(/^[0-9a-f-]{36}$/i, '').replace(/-/g, ' ');
  const label = cleaned || (meaningful.length > 1 ? meaningful[meaningful.length - 2] : 'item');

  return `${action} ${label}`;
}

/** Format a timestamp into a relative time string. */
function formatTimestamp(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default OfflineQueueManager;
